import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, ArrowRight, Sparkles, Lock, Heart } from 'lucide-react';
import Sticker from './Sticker';
import OrganizerLogo from './OrganizerLogo';
import { useSettings } from '../lib/usePublicData';

export const Footer = () => {
    const { data: settings } = useSettings();
    const fbUrl = settings?.social_facebook_url || '#';
    const igUrl = settings?.social_instagram_url || '#';
    const organizerLogoUrl = settings?.organizer_logo_url || '';
    const executorLogoUrl = settings?.executor_logo_url || '';
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-vakjes-dark text-cream py-14 sm:py-20 mt-0"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-10">
                {/* Left */}
                <div>
                    <h3
                        className="font-display zz-outline-title"
                        style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
                    >
                        ZOTTE&nbsp;ZOMER
                    </h3>
                    <p className="mt-3 font-display text-base text-cream/95">
                        Ronse 2026
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-cream/85 max-w-sm">
                        Een organisatie door Bright Events met de steun van Stad Ronse.
                    </p>
                    <div className="mt-5">
                        <Sticker variant="green" tilt="r">
                            <Sparkles size={14} className="mr-1" /> Gratis toegang
                        </Sticker>
                    </div>
                </div>

                {/* Mid */}
                <div className="md:pt-2">
                    <span className="font-display text-xs uppercase tracking-wider text-cream/70">Volg ons</span>
                    <p className="mt-2 text-sm text-cream/85">Ronse Leeft op Facebook & Instagram</p>
                    <div className="mt-4 flex items-center gap-3">
                        <a
                            href={fbUrl}
                            aria-label="Facebook Ronse Leeft"
                            data-testid="footer-social-facebook"
                            target={fbUrl !== '#' ? '_blank' : undefined}
                            rel={fbUrl !== '#' ? 'noopener noreferrer' : undefined}
                            className="w-11 h-11 rounded-full border-2 border-cream/80 flex items-center justify-center hover:bg-cream hover:text-[var(--green-dark)] transition-colors"
                        >
                            <Facebook size={18} />
                        </a>
                        <a
                            href={igUrl}
                            aria-label="Instagram Ronse Leeft"
                            data-testid="footer-social-instagram"
                            target={igUrl !== '#' ? '_blank' : undefined}
                            rel={igUrl !== '#' ? 'noopener noreferrer' : undefined}
                            className="w-11 h-11 rounded-full border-2 border-cream/80 flex items-center justify-center hover:bg-cream hover:text-[var(--green-dark)] transition-colors"
                        >
                            <Instagram size={18} />
                        </a>
                    </div>
                </div>

                {/* Right */}
                <div className="md:pt-2 md:text-right">
                    <Link
                        to="/#word-partner"
                        data-testid="footer-cta-partner"
                        className="inline-flex items-center gap-2 font-display text-base text-cream hover:underline underline-offset-4"
                    >
                        Ook partner worden? <ArrowRight size={18} />
                    </Link>
                    <div className="mt-4">
                        <Link
                            to="/#contact"
                            data-testid="footer-cta-contact"
                            className="inline-flex items-center gap-2 text-sm text-cream/85 hover:text-cream transition-colors"
                        >
                            Naar contact →
                        </Link>
                    </div>
                </div>
            </div>

            {/* Organizer logo row — Bright Events hoofdorganisator (groter), Stad Ronse met de steun van (kleiner) */}
            <div
                data-testid="footer-organizer-row"
                className="mx-auto max-w-7xl px-5 sm:px-8 mt-10 sm:mt-14 pt-8 border-t border-cream/15 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10"
            >
                <div className="flex items-center gap-3">
                    <span className="font-display text-[10px] sm:text-xs uppercase tracking-[0.18em] text-cream/70">
                        Hoofdorganisator
                    </span>
                    <OrganizerLogo url={executorLogoUrl} variant="bright" size="md" onDark testId="footer-org-bright" />
                </div>
                <span className="hidden sm:inline-block w-px h-7 bg-cream/25" aria-hidden="true" />
                <div className="flex items-center gap-3">
                    <span className="font-display text-[10px] sm:text-xs uppercase tracking-[0.18em] text-cream/70">
                        Met de steun van
                    </span>
                    <OrganizerLogo url={organizerLogoUrl} variant="stad" size="sm" onDark testId="footer-org-stad" />
                </div>
            </div>

            <div className="mt-8 sm:mt-10 pt-4 flex flex-col items-center gap-4">
                {/* Legal links row */}
                <nav
                    data-testid="footer-legal-nav"
                    aria-label="Wettelijke pagina's"
                    className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs text-cream/55"
                >
                    <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-cream hover:underline underline-offset-4 transition-colors min-h-[44px] inline-flex items-center px-1">
                        Privacybeleid
                    </Link>
                    <span className="text-cream/30" aria-hidden="true">·</span>
                    <Link to="/cookies" data-testid="footer-link-cookies" className="hover:text-cream hover:underline underline-offset-4 transition-colors min-h-[44px] inline-flex items-center px-1">
                        Cookiebeleid
                    </Link>
                    <span className="text-cream/30" aria-hidden="true">·</span>
                    <Link to="/algemene-voorwaarden" data-testid="footer-link-voorwaarden" className="hover:text-cream hover:underline underline-offset-4 transition-colors min-h-[44px] inline-flex items-center px-1">
                        Algemene voorwaarden
                    </Link>
                    <span className="text-cream/30" aria-hidden="true">·</span>
                    <Link to="/wettelijke-vermeldingen" data-testid="footer-link-imprint" className="hover:text-cream hover:underline underline-offset-4 transition-colors min-h-[44px] inline-flex items-center px-1">
                        Wettelijke vermeldingen
                    </Link>
                </nav>

                {/* Copyright + admin */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                    <p className="text-xs text-cream/60">
                        © 2026 Zotte Zomer Ronse · Stad Ronse × Bright Events
                    </p>
                    <span className="hidden sm:inline text-cream/30" aria-hidden="true">·</span>
                    <a
                        href="/admin/login"
                        data-testid="footer-admin-link"
                        className="inline-flex items-center gap-1.5 text-xs text-cream/55 hover:text-cream hover:underline underline-offset-4 transition-colors"
                    >
                        <Lock size={11} aria-hidden="true" />
                        Beheer
                    </a>
                </div>

                {/* Websitebestellen.be credit */}
                <p
                    data-testid="footer-credit"
                    className="text-xs text-cream/55 inline-flex items-center gap-1.5"
                >
                    Met veel liefde
                    <Heart size={12} className="text-[var(--pink)]" fill="currentColor" aria-hidden="true" />
                    gemaakt door{' '}
                    <a
                        href="https://www.websitebestellen.be"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-credit-link"
                        className="text-cream/85 hover:text-cream hover:underline underline-offset-4 transition-colors"
                    >
                        www.websitebestellen.be
                    </a>
                </p>
            </div>
        </footer>
    );
};

export default Footer;
